import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, BookOpen, Hammer, Target, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { PhaseRecommendations } from "@/components/PhaseRecommendations";

export default function Roadmap() {
  const [roadmaps, setRoadmaps] = useState([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/roadmaps").then((d) => {
      setRoadmaps(d.roadmaps || []);
      setLoading(false);
    });
  }, []);

  const rm = roadmaps[active];

  const toggle = async (key, done) => {
    const updated = await api.patch(`/roadmap/${rm.roadmap_id}/step`, { step_key: key, done });
    setRoadmaps(roadmaps.map((r, i) => (i === active ? updated : r)));
  };

  const remove = async () => {
    await api.del(`/roadmap/${rm.roadmap_id}`);
    setRoadmaps(roadmaps.filter((r) => r.roadmap_id !== rm.roadmap_id));
    setActive(0);
    setConfirming(false);
    toast.success("Roadmap removed");
  };

  if (loading) return <p className="overline" data-testid="roadmap-loading">Loading roadmap</p>;

  if (!rm)
    return (
      <div className="panel p-12 max-w-2xl" data-testid="roadmap-empty">
        <p className="overline">Step 04</p>
        <h1 className="mt-5 font-display text-4xl tracking-tight leading-none">No roadmap yet</h1>
        <p className="mt-5 text-sm text-muted-foreground leading-relaxed">Pick one of your paths and Loop will map it into phases.</p>
        <button onClick={() => navigate("/paths")} data-testid="go-paths-button"
          className="mt-8 px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors duration-200">
          See my paths
        </button>
      </div>
    );

  const total = rm.phases.reduce((a, p) => a + (p.checklist?.length || 0), 0);
  const done = (rm.completed || []).length;

  return (
    <div className="space-y-6" data-testid="roadmap-page">
      <div className="panel p-8 lg:p-10 rise">
        <div className="flex items-start gap-4">
          <div>
            <p className="overline">Step 04 — Your roadmap</p>
            {rm.domain && (
              <span className="mt-4 inline-block text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border border-primary/30 text-primary" data-testid="roadmap-domain">
                {rm.domain}
              </span>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            {confirming ? (
              <>
                <button onClick={remove} data-testid="confirm-delete-roadmap-button"
                  className="text-xs px-4 py-2 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors duration-200">
                  Remove for good
                </button>
                <button onClick={() => setConfirming(false)} data-testid="cancel-delete-roadmap-button"
                  className="text-xs px-4 py-2 rounded-full border border-white/12 text-muted-foreground hover:text-white transition-colors duration-200">
                  Keep it
                </button>
              </>
            ) : (
              <button onClick={() => setConfirming(true)} data-testid="delete-roadmap-button"
                aria-label="Remove this roadmap"
                className="flex items-center gap-2 text-xs px-4 py-2 rounded-full border border-white/12 text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors duration-200">
                <Trash2 className="h-3.5 w-3.5" /> Remove roadmap
              </button>
            )}
          </div>
        </div>
        <h1 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight leading-none">{rm.path_title}</h1>
        <p className="mt-6 text-base text-muted-foreground max-w-3xl leading-relaxed flex items-start gap-3">
          <Target className="h-5 w-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
          {rm.north_star}
        </p>
        <div className="mt-8 flex items-center gap-5">
          <div className="flex-1 h-1.5 bg-white/[0.07]">
            <div className="h-full bg-primary transition-[width] duration-500" style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
          </div>
          <span className="font-display text-sm" data-testid="roadmap-progress">{done}/{total} steps</span>
        </div>
        {roadmaps.length > 1 && (
          <div className="mt-7 flex flex-wrap gap-2">
            {roadmaps.map((r, i) => (
              <button key={r.roadmap_id} onClick={() => setActive(i)} data-testid={`roadmap-switch-${i}`}
                className={`text-xs px-4 py-2 rounded-full border transition-colors duration-200 ${
                  i === active ? "border-primary/60 text-primary" : "border-white/10 text-muted-foreground hover:text-white"
                }`}>
                {r.path_title}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {rm.phases.map((phase, pi) => (
          <div key={pi} className="panel p-8 lg:p-10 rise" style={{ animationDelay: `${pi * 70}ms` }} data-testid={`phase-${pi}`}>
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <div className="flex items-center gap-3">
                  <span className="font-display text-3xl text-primary/70">0{pi + 1}</span>
                  <span className="overline">{phase.timeframe}</span>
                </div>
                <h2 className="mt-4 font-display text-2xl tracking-tight">{phase.name}</h2>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{phase.focus}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {(phase.skills || []).map((s) => (
                    <span key={s} className="text-xs px-3 py-1.5 rounded-full border border-primary/25 text-primary/90">{s}</span>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-4 space-y-6">
                <div>
                  <span className="overline flex items-center gap-2"><Hammer className="h-3.5 w-3.5" /> Projects</span>
                  <ul className="mt-4 space-y-4">
                    {(phase.projects || []).map((pr, i) => (
                      <li key={i}>
                        <p className="text-sm font-medium">{pr.title}</p>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{pr.description}</p>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <span className="overline flex items-center gap-2"><BookOpen className="h-3.5 w-3.5" /> Resources</span>
                  <ul className="mt-4 space-y-2">
                    {(phase.resources || []).map((r, i) => (
                      <li key={i} className="text-sm text-muted-foreground">
                        <span className="text-foreground">{r.title}</span> · {r.type}
                      </li>
                    ))}
                  </ul>
                  <PhaseRecommendations roadmapId={rm.roadmap_id} phaseIndex={pi} />
                </div>
              </div>

              <div className="lg:col-span-4">
                <span className="overline">Checklist</span>
                <ul className="mt-4 space-y-2">
                  {(phase.checklist || []).map((step, si) => {
                    const key = `${pi}-${si}`;
                    const checked = (rm.completed || []).includes(key);
                    return (
                      <li key={key}>
                        <button onClick={() => toggle(key, !checked)} data-testid={`step-${pi}-${si}`}
                          className="w-full flex items-start gap-3 text-left px-3 py-2.5 rounded-sm hover:bg-white/[0.05] transition-colors duration-200">
                          <span className={`mt-0.5 h-4 w-4 shrink-0 border flex items-center justify-center transition-colors duration-200 ${
                            checked ? "bg-primary border-primary" : "border-white/25"
                          }`}>
                            {checked && <Check className="h-3 w-3 text-primary-foreground" strokeWidth={3} />}
                          </span>
                          <span className={`text-sm leading-relaxed ${checked ? "line-through text-muted-foreground" : ""}`}>{step}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
